<template>
  <mdb-container>
    <mdb-card>
      <mdb-card-body @keypress.enter="search">
        <mdb-card-title>評価一覧</mdb-card-title>
        <mdb-row>
          <mdb-col col="auto" class="py-2">
            <mdb-radio label="案件" v-model="mode" value="project" inline />
            <mdb-radio label="評価" v-model="mode" value="review" @click="gotoReview" inline />
          </mdb-col>
          <mdb-col><client-selector label="クライアント名" v-model="cond.client_name" /></mdb-col>
          <mdb-col><marketer-selector label="マーケター名" v-model="cond.marketer_id" :init-id="cond.marketer_id" /></mdb-col>
          <mdb-col><project-tag-selector label="業務内容" v-model="cond.tag" /></mdb-col>
          <mdb-col><month-picker label="契約開始月" v-model="cond.contract_start"></month-picker></mdb-col>
        </mdb-row>
        <div class="clearfix text-center">
          <mdb-btn color="primary" class="m-2" @click="search(true)">検索する</mdb-btn>
          <mdb-btn color="light" class="m-2" @click="reset">リセットする</mdb-btn>
        </div>

        <div class="mt-3" border="light">
          <div class="mb-2 text-muted">検索結果（{{ total }}件）</div>

            <mdb-table>
              <thead>
                <tr>
                  <th>クライアント</th>
                  <th>マーケター</th>
                  <th>契約業務</th>
                  <th>総合<table-order order="total" :current-order="order" :current-dirc="dirc" @sort="sort" /></th>
                  <th>スキル<table-order order="skill" :current-order="order" :current-dirc="dirc" @sort="sort" /></th>
                  <th>人柄<table-order order="personal" :current-order="order" :current-dirc="dirc" @sort="sort" /></th>
                  <th>評価者</th>
                  <th>評価登録日<table-order order="answered_at" :current-order="order" :current-dirc="dirc" @sort="sort" /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="review in reviews" :key="review.id">
                  <td>{{ review.client_project.client.name }}</td>
                  <td>{{ review.client_project.marketer?.family_name }} {{ review.client_project.marketer.first_name }}</td>
                  <td>{{ review.client_project.title }}</td>
                  <td>{{ review.total ?? '--' }}</td>
                  <td>{{ review.skill ?? '--' }}</td>
                  <td>{{ review.personal ?? '--' }}</td>
                  <td>{{ review.answer_name }}</td>
                  <td>{{ review.answered_at }}</td>
                  <td>
                    <mdb-btn color="primary" @click="show(review)" floating>詳細</mdb-btn>
                    <mdb-tooltip v-if="staff.role == 'admin'" v-model="review.delTip">
                      <template #reference>
                        <mdb-btn @click="remove(review)" floating><mdb-icon icon="times" /></mdb-btn>
                      </template>
                      <template #tip>評価削除</template>
                    </mdb-tooltip>
                  </td>
                </tr>
              </tbody>
            </mdb-table>

            <div class="mt-5 d-flex justify-content-center">
              <nav>
                <mdb-pagination>
                  <mdb-page-nav prev icon @click="prev" v-if="page > 1" />
                  <mdb-page-item v-for="p in pages" :key="'page'+p" @click="page = p; search()" :active="page == p">{{ p }}</mdb-page-item>
                  <mdb-page-nav next icon @click="next" v-if="page < lastPage" />
                </mdb-pagination>
              </nav>
            </div>

        </div>
      </mdb-card-body>
    </mdb-card>
  </mdb-container>

  <review-detail-modal v-model="modalDetail" :review="review" @edit="edit" @project-edit="modalProject = true" />
  <project-review-modal v-model="modalEdit" :project="review.client_project" :init-review="review" @save="search(true)"></project-review-modal>
  <project-modal v-model="modalProject" :project="review.client_project" @save="search(true)"></project-modal>
</template>

<script>
import { computed, ref, watch } from "vue";
import axios from "axios";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import { useStaff } from "@/common/staff";
import ClientSelector from "@/components/form/ClientSelector.vue";
import MarketerSelector from "@/components/form/MarketerSelector.vue";
import ProjectTagSelector from "@/components/form/ProjectTagSelector.vue";
import ReviewDetailModal from "@/components/modal/ReviewDetailModal.vue";
import ProjectReviewModal from "@/components/modal/ProjectReviewModal.vue";
import ProjectModal from "@/components/modal/ProjectModal.vue";
import TableOrder from "@/components/TableOrder.vue";
import MonthPicker from "@/components/form/MonthPicker.vue";

export default {
  layout: "staff",
  components: {
    ClientSelector,
    MarketerSelector,
    ProjectTagSelector,
    ProjectModal,
    ReviewDetailModal,
    ProjectReviewModal,
    TableOrder,
    MonthPicker,
  },
  setup() {
    const reviews = ref([]);
    const users = ref([]);
    const total = ref(0);
    const store = useStore();
    const cond = ref({...store.state.clientProjectSearchCond});
    watch(() => cond, () => {
      store.dispatch("setCompanySearchCond", cond.value);
    }, { deep: true });
    const order = ref("answered_at");
    const dirc = ref("desc");

    const router = useRouter();
    const route = useRoute();

    const { staff } = useStaff(true);

    const mode = ref("review");
    watch(() => mode.value, () => {
      if (mode.value == "project") {
        router.push({ path: "/staff/clients/projects/", query: {
          client_name: cond.value.client_name, marketer_id: cond.value.marketer_id } });
      }
    });

    const page = ref(store.state.clientProjectSearchCond);
    watch(() => page.value, () => {
      store.dispatch("setClientProjectSearchPage", page.value);
    });
    const limit = 20;
    const lastPage = computed(() => {
      const lp = Math.ceil(total.value / limit);
      return lp > 0 ? lp : 1;
    });
    const pages = computed(() => {
      let p = 1;
      let ps = [];
      while (p <= lastPage.value) {
        ps.push(p);
        p++;
      }
      return ps;
    });

    if (route.query.client_name) cond.value.client_name = route.query.client_name;
    if (route.query.marketer_id) cond.value.marketer_id = parseInt(route.query.marketer_id, 10);
    if (route.query.title) cond.value.title = route.query.title;

    const search = async (force=false) => {
      if (force) page.value = 1;
      let r = await axios.get("/api/staff/clients/projects/reviews/", {
        params: { ...cond.value, page: page.value, order: order.value, dirc: dirc.value, limit }
      });
      reviews.value = r.data.client_project_reviews;
      total.value = r.data.total;
    };
    search();

    const sort = async (sortCond) => {
      order.value = sortCond.order;
      dirc.value = sortCond.dirc;
      await search();
    };

    const reset = async () => {
      store.dispatch("resetClientProjectSearchCond");
      cond.value = {...store.state.clientProjectSearchCond};
      await search(true);
    };

    const prev = async () => {
      page.value--;
      await search();
    };

    const next = async () => {
      page.value++;
      await search();
    };

    const review = ref({});
    const modalDetail = ref(false);
    const modalEdit = ref(false);
    const modalProject = ref(false);

    function show(rv) {
      review.value = rv;
      modalDetail.value = true;
    }

    function edit() {
      modalEdit.value = true;
    }

    async function remove(review) {
      if (confirm("評価を削除します。よろしいですか？")) {
        let r = await axios.delete(`/api/staff/clients/projects/${review.client_project_id}/reviews/${review.id}`);
        if (r.data.review_count == 0) {
          if (confirm(`案件『${review.client_project.title}』の評価が0件になりました。案件を削除しますか？`)) {
            await axios.delete(`/api/staff/clients/projects/${review.client_project_id}`);
          }
        }
        search(true);
      }
    }

    return {
      reviews, users, total, cond, order, dirc, page, pages, lastPage, mode, review, modalDetail, modalEdit, modalProject, staff,
      search, sort, reset, prev, next, show, edit, remove,
    };
  },
};
</script>
